import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import type { ISpProfilePublic } from '@brushfire/types'
import { ProfilePictureUpload } from '@/features/account/components/ProfilePictureUpload'
import accIcon from '@/features/account/assets/acc-icon.svg'
import { formatLocation } from '@/features/account/lib/account-display-utils'
import { formatUsNanpDisplay } from '@/features/account/lib/account-phone-utils'
import {
  ACCOUNT_BANNER_MAX_WIDTH,
  ACCOUNT_PROFILE_AVATAR_OFFSET_PX,
  ACCOUNT_PROFILE_BANNER_OVERLAP_PX,
  accountAvatarEmptyViewSx,
  accountAvatarImageViewSx,
  accountContentColumnSx,
  accountEditAvatarSectionSx,
  accountProfileBannerSx,
  accountProfileContactRowSx,
  accountProfileEditButtonSx,
  accountProfileEmailSpacingSx,
  accountProfileEmailSx,
  accountProfileLocationSx,
  accountProfileNameSx,
  accountProfilePanelSx,
  accountProfilePhoneSx,
} from '@/features/account/lib/account-settings-tokens'
import { RiMapPinFill, RiPencilLine, RiPhoneFill } from '@/shared/icons/remix'

interface Props {
  profile: ISpProfilePublic
  isEditing: boolean
  onEdit: () => void
  onAvatarUpload: (file: File) => Promise<void>
  isAvatarUploading?: boolean
}

function ProfileAvatar({ profile }: Readonly<{ profile: ISpProfilePublic }>) {
  const hasAvatar = Boolean(profile.avatarUrl)
  const avatarFrameSx = hasAvatar ? accountAvatarImageViewSx : accountAvatarEmptyViewSx

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <Box sx={avatarFrameSx} data-testid="account-profile-avatar">
        {hasAvatar ? (
          <Box
            component="img"
            src={profile.avatarUrl ?? undefined}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            component="img"
            src={accIcon}
            alt=""
            aria-hidden
            sx={{ width: 68, height: 68 }}
          />
        )}
      </Box>
    </Box>
  )
}

export function AccountProfileCard({
  profile,
  isEditing,
  onEdit,
  onAvatarUpload,
  isAvatarUploading = false,
}: Readonly<Props>) {
  const locationLabel = formatLocation(profile)

  if (isEditing) {
    return (
      <Box data-testid="account-profile-card" sx={accountEditAvatarSectionSx}>
        <ProfilePictureUpload
          profile={profile}
          onUpload={onAvatarUpload}
          isUploading={isAvatarUploading}
        />
      </Box>
    )
  }

  return (
    <Box data-testid="account-profile-card">
      <Box sx={{ pt: '70px' }}>
        <Box sx={{ maxWidth: ACCOUNT_BANNER_MAX_WIDTH, mx: 'auto' }}>
          <Box sx={accountProfileBannerSx} />
        </Box>
      </Box>

      <Box
        sx={{
          ...accountContentColumnSx,
          position: 'relative',
          mt: `-${ACCOUNT_PROFILE_BANNER_OVERLAP_PX}px`,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: `-${ACCOUNT_PROFILE_AVATAR_OFFSET_PX}px`,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          }}
        >
          <ProfileAvatar profile={profile} />
        </Box>

        <Box sx={accountProfilePanelSx}>
          <IconButton
            aria-label="Edit account details"
            onClick={onEdit}
            data-testid="account-edit-pencil"
            sx={accountProfileEditButtonSx}
          >
            <RiPencilLine size={24} />
          </IconButton>
          <Typography sx={accountProfileNameSx}>
            {profile.firstName} {profile.lastName}
          </Typography>
          <Typography sx={{ ...accountProfileEmailSx, ...accountProfileEmailSpacingSx }}>
            {profile.email}
          </Typography>

          {locationLabel || profile.phone ? (
            <Box sx={accountProfileContactRowSx}>
              {locationLabel ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '19px' }}>
                  <RiMapPinFill size={24} color="#0d9467" aria-hidden />
                  <Typography sx={accountProfileLocationSx}>{locationLabel}</Typography>
                </Box>
              ) : (
                <Box />
              )}
              {profile.phone ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <RiPhoneFill size={24} color="#df6534" aria-hidden />
                  <Typography sx={accountProfilePhoneSx}>
                    {formatUsNanpDisplay(profile.phone)}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}
